import { inject, injectable } from 'inversify';

import {
  Enums,
  SharedModels,
  TenantModels,
  UIDtoFactory,
} from '@project44-models/iam-gateway';

import { ServiceClientAdapter } from '../../../../shared/abstract/infrastructrue/ServiceClientAdapter';
import { TRepositoryRequestPayload } from '../../../../shared/abstract/infrastructrue/types';
import { AxiosDelegationStrategyEnum } from '../../../../shared/core/domain/enums';
import { ParametersValueObject } from '../../../../shared/core/domain/ParameterValueObject';
import { ValueObject } from '../../../../shared/core/domain/ValueObject';
import { InternalAxiosConfigAdapter } from '../../../../shared/core/infrastructrue/adapters/InternalAxiosConfig.adapter';
import { UnifiedRolesAdapter } from '../../../UnifiedRoles/infrastructure/adapters/UnifiedRoles.adapter';
import {
  ITenantServiceClientAdapter,
  SwitchTenantPayload,
  SwitchTenantResponse,
  TAllTenantAgnosticUnifiedRolesResponse,
  TenantQueryPayload,
  TenantQueryResponse,
} from '../../domain';

@injectable()
export class TenantServiceClientAdapter
  extends ServiceClientAdapter
  implements ITenantServiceClientAdapter
{
  constructor(
    @inject(InternalAxiosConfigAdapter)
    private readonly axiosConfigAdapter: InternalAxiosConfigAdapter,

    @inject(UnifiedRolesAdapter)
    private readonly unifiedRolesAdapter: UnifiedRolesAdapter,
  ) {
    super();
  }

  public async query<K extends AxiosDelegationStrategyEnum>(
    payload: ParametersValueObject<TenantQueryPayload>,
  ): Promise<ValueObject<TenantQueryResponse<K>>> {
    const { tenantQueryDto, axiosDelegationStrategy } = payload.parameters;

    const requestConfig: TRepositoryRequestPayload<TenantModels.TenantQueryResultsViewModel> =
      {
        config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
          url: '/p44-admins/tenants',
          method: 'GET',
          urlSearchParams: new URLSearchParams(
            Object.entries(tenantQueryDto.toSerializedModel()),
          ),
          dtoAdapter(data) {
            return UIDtoFactory.create(
              Enums.UiDtoFactoryEnum.TENANT_QUERY_RESULTS,
              data,
            );
          },
        }),
        strategy: axiosDelegationStrategy,
      };

    return this.handleRequest<TenantModels.TenantQueryResultsViewModel, K>(
      ParametersValueObject.create(requestConfig),
    );
  }

  public async switchTenant<K extends AxiosDelegationStrategyEnum>(
    payload: SwitchTenantPayload,
  ): Promise<ValueObject<SwitchTenantResponse<K>>> {
    const { tenantDto, axiosDelegationStrategy } = payload;

    const requestConfig: TRepositoryRequestPayload<TenantModels.TenantViewModel> =
      {
        config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
          url: `/authentication/tenants/${tenantDto.source.getTenantUuid()}/auth`,
          method: 'POST',
          dtoAdapter() {
            return tenantDto;
          },
        }),
        strategy: axiosDelegationStrategy,
      };

    return this.handleRequest<TenantModels.TenantViewModel, K>(
      ParametersValueObject.create(requestConfig),
    );
  }

  public async getAllTenantAgnosticUnifiedRoles(): Promise<
    ValueObject<
      TAllTenantAgnosticUnifiedRolesResponse<AxiosDelegationStrategyEnum.EXECUTABLE>
    >
  > {
    const requestConfig: TRepositoryRequestPayload<SharedModels.UnifiedRoleCollectionViewModel> =
      {
        config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
          url: `/admins/tenants/roles/tenant-agnostic`,
          method: 'GET',
          dtoAdapter: this.unifiedRolesAdapter.toUnifiedRoleCollectionDto,
        }),
        strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
      };

    return this.handleRequest<
      SharedModels.UnifiedRoleCollectionViewModel,
      AxiosDelegationStrategyEnum.EXECUTABLE
    >(ParametersValueObject.create(requestConfig));
  }

  public readonly getTenantUnifiedRoles: ITenantServiceClientAdapter['getTenantUnifiedRoles'] =
    async (payload) => {
      const requestConfig: TRepositoryRequestPayload<SharedModels.UnifiedRoleCollectionViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/admins/tenants/${payload.tenantDto.source.getTenantUuid()}/roles`,
            method: 'GET',
            dtoAdapter: this.unifiedRolesAdapter.toUnifiedRoleCollectionDto,
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      return this.handleRequest<
        SharedModels.UnifiedRoleCollectionViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));
    };

  public readonly getTenantByUuid: ITenantServiceClientAdapter['getTenantByUuid'] =
    async (payload) => {
      const requestConfig: TRepositoryRequestPayload<TenantModels.TenantViewModel> =
        {
          config: this.axiosConfigAdapter.toModifiedAxiosRequestConfig({
            url: `/p44-admins/tenants/${payload.tenantUuid}`,
            method: 'GET',
            urlSearchParams: new URLSearchParams([
              ['status', payload.tenantStatus],
            ]),
            dtoAdapter: (data) => {
              return UIDtoFactory.create(Enums.UiDtoFactoryEnum.TENANT, data);
            },
          }),
          strategy: AxiosDelegationStrategyEnum.EXECUTABLE,
        };

      const { result } = await this.handleRequest<
        TenantModels.TenantViewModel,
        AxiosDelegationStrategyEnum.EXECUTABLE
      >(ParametersValueObject.create(requestConfig));

      return result;
    };
}
